import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { Box, Button } from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import YesNoPrompt from "components/modals/prompts/yesNoPrompt";
import { ATTACHMENT_LEVEL } from "constants/generalConstants";
import useAttachments from "hooks/useAttachments";
import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { eliminateAttachmentByFileId } from "services/attachment/attachmentAPIs";

import AttachmentTable from "./attachmentTable";

const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    "&:not(:last-child)": {
        borderBottom: 0,
    },
    "&::before": {
        display: "none",
    },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, .05)"
            : "rgba(0, 0, 0, .03)",
    flexDirection: "row-reverse",
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
        transform: "rotate(90deg)",
    },
    "& .MuiAccordionSummary-content": {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(0.5),
    borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const LinesAttachmentsReview = () => {
    const reqLines = useSelector(
        (state) => state.createRequisition.iBuyRequisition.requisitionLines
    );

    const isPostPoReq = useSelector(
        (state) => state.createRequisition.isPostPoReq
    );
    const { lineAttachments, getAttachmentsByLineId, deleteAttachment } =
        useAttachments();

    const dispatch = useDispatch();

    const [openDeletePopup, setOpenDeletePopup] = useState(false);

    // Checks if any attachment is selected for deletion
    const hasAnyAttachmentSelected = () => {
        const selectedAttachmentsCount = lineAttachments.filter(
            (attachment) => attachment.isSelectedToDelete === true
        ).length;

        return (
            selectedAttachmentsCount !== lineAttachments.length &&
            selectedAttachmentsCount !== 0
        );
    };

    const handleDeleteButtonClick = () => {
        setOpenDeletePopup(true);
    };

    const handleConfirmDeleteNo = () => {
        setOpenDeletePopup(false);
    };

    const handleConfirmDeleteYes = async () => {
        setOpenDeletePopup(false);

        // Delete all attachments if none is selected
        let selectedAttachmentsToDelete = lineAttachments;
        if (hasAnyAttachmentSelected()) {
            selectedAttachmentsToDelete = lineAttachments.filter(
                (attachment) => attachment.isSelectedToDelete
            );
        }

        selectedAttachmentsToDelete = selectedAttachmentsToDelete.filter(
            (a) => (isPostPoReq && a.isNewUpload) || !isPostPoReq
        );

        selectedAttachmentsToDelete.forEach(async (attachment) => {
            await deleteAttachment(attachment, dispatch);
        });
    };

    const anyReqLineHasAttachments = lineAttachments?.length > 0;
    const noNewUploads =
        lineAttachments.length ===
        lineAttachments.filter((a) => !a.isNewUpload).length;

    if (!anyReqLineHasAttachments) {
        return (
            <Typography align="center">
                No attachments in requisition lines.
            </Typography>
        );
    }

    return (
        <>
            <Box sx={{ paddingBottom: 2 }}>
                <Button
                    variant="outlined"
                    size="small"
                    color="info"
                    disabled={
                        !anyReqLineHasAttachments ||
                        (isPostPoReq && noNewUploads)
                    }
                    onClick={handleDeleteButtonClick}
                >
                    {hasAnyAttachmentSelected()
                        ? "Delete selected line attachments"
                        : "Delete all line attachments"}
                </Button>
            </Box>
            <Box>
                {reqLines &&
                    reqLines.map((reqLine, index) => {
                        const labelId = `panel${index}`;
                        const reqLineAttachments =
                            getAttachmentsByLineId(index);
                        return (
                            <Accordion key={index}>
                                <AccordionSummary
                                    aria-controls={`${labelId}-content`}
                                    id={`${labelId}-header`}
                                >
                                    <Typography>
                                        Line ID: {index + 1}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {reqLineAttachments?.length === 0 ? (
                                        <Typography align="center">
                                            {`No attachments in line #${
                                                index + 1
                                            }`}
                                        </Typography>
                                    ) : (
                                        <AttachmentTable
                                            attachments={
                                                reqLineAttachments ?? []
                                            }
                                        />
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
            </Box>
            <YesNoPrompt
                title="Delete Confirmation"
                subtitle={
                    hasAnyAttachmentSelected()
                        ? "Are you sure you want to delete selected attachments from the requisition lines?"
                        : "Are you sure you want to delete all attachments from the requisition lines?"
                }
                promptOpen={openDeletePopup}
                setPromptOpen={handleConfirmDeleteNo}
                onClickYes={() => {
                    handleConfirmDeleteYes();
                }}
            />
        </>
    );
};

LinesAttachmentsReview.propTypes = {
    reqLineItems: PropTypes.array,
};

export default LinesAttachmentsReview;
